"use client";

import { useState, useEffect } from "react";
import { useLanguage } from "../../hooks/useLanguage";
import { Video } from "lucide-react";
import config from "../../config/config";
import SectionHeader from "./SectionHeader";
import VideoCard from "../shared/VideoCard";
import PostsSkeleton from "../posts/PostsSkeleton";

export default function VideoSection() {
  const [videos, setVideos] = useState([]);
  const [videoSettings, setVideoSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const { language } = useLanguage();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch homepage settings for video section
        const settingsResponse = await fetch(`${config.NEXT_PUBLIC_API_URL}/api/public/settings/homepage/video`);
        const settingsData = await settingsResponse.json();

        if (settingsData.success && settingsData.data) {
          setVideoSettings(settingsData.data);

          // Only fetch videos if section is enabled
          if (settingsData.data.enabled !== false) {
            const videosResponse = await fetch(
              `${config.NEXT_PUBLIC_API_URL}/api/public/p/video?limit=8`
            );

            if (videosResponse.ok) {
              const videosData = await videosResponse.json();
              if (videosData.success) {
                setVideos(videosData.data.posts ? videosData.data.posts : []);
              }
            }
          }
        }
      } catch (error) {

      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [language]);

  const getLabel = (field) => {
    if (!field) return '';
    if (typeof field === 'string') return field;
    if (typeof field === 'object') {
      return field[language] ? field[language] : (field.en ? field.en : '');
    }
    return '';
  };

  if (loading) {
    return (
      <section className="py-6 sm:py-8 md:py-10 lg:py-12 xl:py-14 px-2 sm:px-3 md:px-4 lg:px-6">
        <div className="max-w-7xl mx-auto w-full">
          <SectionHeader
            icon={Video}
            sectionTitle="Videos"
            mainHeading="Latest Videos"
          />
          <PostsSkeleton />
        </div>
      </section>
    );
  }

  // Hide section if disabled or no meaningful content
  if (videoSettings.enabled === false) {
    return null;
  }
  if (!getLabel(videoSettings.sectionTitle) && !getLabel(videoSettings.sectionSubtitle) && videos.length === 0) {
    return null;
  }

  return (
    <section className="py-6 sm:py-8 md:py-10 lg:py-12 xl:py-14 px-2 sm:px-3 md:px-4 lg:px-6" style={{ background: 'linear-gradient(135deg, var(--color-muted) 0%, var(--color-primary)/5% 50%, var(--color-muted) 100%)' }}>
      <div className="max-w-7xl mx-auto w-full">
        <SectionHeader
          icon={Video}
          sectionTitle={videoSettings.sectionTitle}
          mainHeading={videoSettings.sectionSubtitle}
          description={videoSettings.description}
          buttonText={videoSettings.viewAllButtonText}
          buttonLink={videos.length > 0 && videoSettings.showViewAllButton !== false ? (videoSettings.viewAllButtonLink || '/video') : null}
          buttonTarget={videoSettings.viewAllButtonTarget || '_self'}
          getLabel={getLabel}
        />

        {/* Videos Grid - YouTube Style */}
        <div className="mobile-horizontal-scroll grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3 md:gap-4 lg:gap-6">
          {videos.map((video, index) => (
            <VideoCard key={video._id || video.id} video={video} />
          ))}
        </div>


      </div>
    </section>
  );
}
